import styled from '@emotion/styled';
import { CardBorder } from '@shared/components/core/card/CommonCardStyles';
import { Switch } from '@shared/components/core/field/CommonFieldStyles';
import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import Icons from '@shared/components/core/images/Icons';
import { BodyMedium, BodySmall, TitleXSmall } from '@shared/components/core/text/Typography';
import TinyTag from '@shared/components/core/tiny/TinyTag';
import OrganizationModel from '@shared/models/organizations/OrganizationModel';
import { useTranslation } from 'react-i18next';

interface IOrgManagedControlsSectionProps {
    organization: OrganizationModel;
}

/**
 * Read-only view of the organization controls that only Cephable can change.
 *
 * These exist for enterprise customers who need to demonstrate — to their own security or procurement
 * teams — both that a setting holds and that nobody in their organization can quietly flip it back.
 * Showing them as locked switches makes the current value and its immutability visible at a glance;
 * the portal never submits them, and the API leaves them off the organization update request entirely.
 *
 * The switch is decorative — a disabled control is announced poorly — so the value is also carried as
 * text, in the adjacent tag and in each row's description.
 */
export default function OrgManagedControlsSection({ organization }: Readonly<IOrgManagedControlsSectionProps>) {
    const { t } = useTranslation('common');

    // Absent on older API responses, which means email was never disabled for the organization.
    const allowsEmails = !organization.disableCephableEmails;
    const isScimEnabled = !!organization.isScimEnabled;

    return (
        <>
            <SubSectionRow
                title={t('organization.settings.managed.title')}
                description={t('organization.settings.managed.description')}
            />
            <ManagedCard>
                <ManagedRow>
                    <div>
                        <TitleXSmall>{t('organization.settings.managed.email.title')}</TitleXSmall>
                        <BodySmall>
                            {allowsEmails
                                ? t('organization.settings.managed.email.allowedDescription')
                                : t('organization.settings.managed.email.blockedDescription')}
                        </BodySmall>
                    </div>
                    <StateWrap>
                        <TinyTag
                            title={t('organization.settings.managed.email.title')}
                            value={allowsEmails ? t('on') : t('off')}
                            hideLabel
                        />
                        <Switch checked={allowsEmails} disabled aria-hidden="true" />
                    </StateWrap>
                </ManagedRow>

                {isScimEnabled && (
                    <ManagedRow>
                        <div>
                            <TitleXSmall>{t('organization.settings.managed.scim.title')}</TitleXSmall>
                            <BodySmall>{t('organization.settings.managed.scim.description')}</BodySmall>
                        </div>
                        <StateWrap>
                            <TinyTag title={t('organization.settings.managed.scim.title')} value={t('on')} hideLabel />
                            <Switch checked disabled aria-hidden="true" />
                        </StateWrap>
                    </ManagedRow>
                )}

                <LockedNote>
                    <Icons name="settings" />
                    <BodyMedium>{t('organization.settings.managed.lockedNote')}</BodyMedium>
                </LockedNote>
            </ManagedCard>
        </>
    );
}

const ManagedCard = styled(CardBorder)`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
`;

const ManagedRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;

    & > div:first-of-type {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
    }
`;

const StateWrap = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
`;

const LockedNote = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    border-top: 1px solid ${(props) => props.theme.color.border};
    padding-top: 0.75rem;
`;
